import axios from 'axios'

import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { VITE_OLYMPUS_URL } from '@/OlympusClient'

/**
 * Hard cap on the ME2 sync so a slow/hung Olympus→ME2 hop can never hold the
 * edit dialog open — on timeout axios rejects with ECONNABORTED, which the
 * best-effort catch below swallows like any other sync hiccup. 10s leaves
 * headroom for the Olympus resolve → ME2 apply → Temporal signal round-trip.
 */
const ME2_EDIT_PUSH_TIMEOUT_MS = 10_000

/**
 * Best-effort convergent push of an edited one-time (custom) campaign's desired
 * state to ME2, via the stateless Olympus passthrough endpoint. Call this AFTER
 * the ME1 edit (updateCampaign + publishChanges) has committed, so Olympus
 * re-resolves the freshly published template subject/sections and the campaign
 * audience rather than a mid-edit snapshot.
 *
 * The Olympus endpoint is stateless and ME2 no-ops for a campaign it never
 * dispatched, so this is safe to fire for any custom email campaign edit (the
 * caller still gates on the `hermes.customCampaignsMe2Edits` flag). Errors are
 * logged and swallowed — an ME2 sync hiccup must never block the edit or the
 * dialog close: ME1 remains the source of truth.
 *
 * Transport is a bare authed axios call (mirroring `ScoutView`'s `api()`) rather
 * than the generated typed client, so reaching this new endpoint needs no
 * olympus-client regeneration: the generated client exposes only typed routes,
 * no generic POST. The web auth cookie rides `withCredentials`, and the standard
 * audience/client/CSRF headers match what `OlympusClient` sends.
 *
 * Deliberately carries only `subject` and `locationId` — never a schedule.
 * Reschedule keeps its own `/schedule` trigger, and the Olympus/ME2 edit path
 * leaves a live send timer untouched when no schedule is supplied.
 *
 * @param campaignId the one-time campaign being edited
 * @param edit the convergent desired state (subject and/or audience)
 * @param edit.subject the edited email subject line
 * @param edit.locationId the edited audience location scope (null = brand-wide)
 */
export async function sendMe2ConvergentEdit(
  campaignId: string,
  edit: { subject?: string; locationId?: string | null }
): Promise<void> {
  try {
    const authStore = useAuthStore()
    const instance = axios.create({
      baseURL: VITE_OLYMPUS_URL,
      timeout: ME2_EDIT_PUSH_TIMEOUT_MS,
      withCredentials: true,
    })
    instance.interceptors.request.use((req) => {
      req.headers['X-PB-Audience'] = 'hermes'
      req.headers['X-PB-Client'] = 'web'
      if (authStore.csrfToken) {
        req.headers['X-CSRFToken'] = authStore.csrfToken
      }
      return req
    })
    await instance.post(
      `/marketing/v1/campaigns/${campaignId}/edit-me2-custom-campaign`,
      edit
    )
  } catch (error) {
    logError(error)
  }
}
